<template>
    <div v-if="subAccount" class="rounded border border-base-300 px-2 pb-2 flex flex-col h-full">
        <div class="flex items-baseline gap-2 py-2">
            <div class="text-primary text-lg truncate overflow-hidden text-ellipsis" :title="subAccount.name">
                <sub-account-link :uuid="subAccount.uuid">{{ subAccount.name }}</sub-account-link>
            </div>
            <div class="grow"/>
            <template v-if="!subAccountFilter.isOpen">
                <div class="flex items-center gap-1 w-[48px] justify-start">
                    <div class="text-base-content/50 text-sm">Inn</div>
                </div>

                <div class="flex w-[120px] justify-end text-lg pr-2">
                    {{ formatCurrency(ledgerEntry.incoming) }}
                </div>
            </template>
            <div
                v-if="showMagicWand && chosenSubAccountLines.length === 0"
                class="cursor-pointer"
                title="Automatisk lukk poster med matchende beløp"
                @click="handleAutoConnect"
            >
                🪄
            </div>
            <div v-if="chosenSubAccountLines.length > 0">Sum: {{ formatCurrency(remainder) }}</div>

            <UButton
                color="neutral"
                variant="outline"
                :label="subAccount.relationType === SUPPLIER ? 'Legg til betaling' : 'Registrer betaling'"
                :disabled="isRegisterPaymentDisabled"
                @click="() => createPaymentTransaction()"
            />

            <UButton
                color="neutral"
                variant="outline"
                label="Lukk poster"
                :disabled="!saccLineConnectionBuilder.isValid()"
                @click="matchLines"
            />
        </div>
        <sub-account-ledger-grid
            ref="ledgerGrid"
            class="h-full"
            :ledger-entry="ledgerEntry"
            :sub-account-filter="subAccountFilter"
            :is-not-connected="(line: SubAccountLine) => saccLineConnectionBuilder.isNotConnected(line)"
            :auto-connect="autoConnect"
            :is-customer="isCustomer"
            @create-payment-transaction="createPaymentTransaction"
            @set-chosen-lines="handleChosenLines"
            @magic-wand="handleMagicWand"
        />
        <div class="flex justify-end items-center py-1 gap-2">
            <template v-if="!subAccountFilter.isOpen">
                <div class="flex items-center gap-1">
                    <div class="text-base-content/50 text-sm">Endringer</div>
                </div>
                <div class="flex text-lg mr-24 ">
                    {{ formatCurrency(ledgerEntry.movement) }}
                </div>
            </template>

            <div class="flex items-center gap-1">
                <div class="text-base-content/50 text-sm">Sum</div>
            </div>
            <div class="flex text-lg pr-2">
                {{ formatCurrency(ledgerEntry.outgoing) }}
            </div>
        </div>
    </div>
    <div v-else>...laster</div>
</template>

<script setup lang="ts">
import {computed, inject, Ref, ref} from 'vue'
import SubAccountLedgerGrid from '@/subaccount/outstandingBalance/SubAccountLedgerGrid.vue'
import {SubAccountLedgerFilter} from '@/subaccount/outstandingBalance/SubAccountLedgerFilter'
import SubAccountLink from '@/subaccount/components/SubAccountLink.vue'
import {LedgerSubAccount} from '@/subaccount/types/SubAccountLedger'
import {findSubAccountByUuid, useSubAccounts} from '@/subaccount/subAccount.service'
import {formatCurrency} from '@/services/formatter.service'
import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
import {SaccLineConnBuilder} from '@/subaccount/outstandingBalance/SaccLineConnBuilder'
import emitter from '@/services/infrastructure/emitter'
import {SACC_LINES_UPDATED} from '@/types/transaction/TransactionEvent'
import {useSubAccountLineService} from '@/subaccount/subAccountLine.service'
import {SUPPLIER} from '@/subaccount/types/SubAccount'

const {closePosts} = useSubAccounts()
const {registerPayments} = useSubAccountLineService()
const ledgerGrid = ref<InstanceType<typeof SubAccountLedgerGrid> | null>(null)

const panelId: Ref<number> | undefined = inject('panelId', ref(1))

const props = defineProps({
    ledgerEntry: {
        type: Object as () => LedgerSubAccount,
        required: true
    },
    subAccountFilter: {
        type: Object as () => SubAccountLedgerFilter,
        required: true
    },
    isCustomer: {
        type: Boolean,
        required: true
    }
})

const chosenSubAccountLines = ref<SubAccountLine[]>([])
const saccLineConnectionBuilder = new SaccLineConnBuilder()
const remainder = ref(0)
const showMagicWand = ref(false)
const autoConnect = ref(false)
const subAccount = computed(() => findSubAccountByUuid(props.ledgerEntry.subAccountUuid))

const isRegisterPaymentDisabled = computed(() => {
    const noLinesSelected = chosenSubAccountLines.value.length === 0
    const hasClosedLine = chosenSubAccountLines.value.some(line => !line.isOpen)
    const hasNegativeAmount = chosenSubAccountLines.value.some(line => line.amount < 0)
    const hasPositiveAmount = chosenSubAccountLines.value.some(line => line.amount > 0)
    const noInvoiceUuid = chosenSubAccountLines.value.every(line => !line.invoiceUuid)
    const hasPendingTransactionLines = chosenSubAccountLines.value.some(line => line.pendingTransactions.length > 0)
    const anyRemainingIsZero = chosenSubAccountLines.value.some(line => line.remainingAmount === 0)

    return noLinesSelected || hasClosedLine || (props.isCustomer ? hasNegativeAmount : hasPositiveAmount) || noInvoiceUuid || hasPendingTransactionLines || anyRemainingIsZero
})

const handleChosenLines = (subAccountLines: SubAccountLine[]) => {
    chosenSubAccountLines.value = subAccountLines
    saccLineConnectionBuilder.addAll(subAccountLines)
    remainder.value = saccLineConnectionBuilder.getRemainder()
}

const handleMagicWand = (shouldShowMagicWand: boolean) => {
    showMagicWand.value = shouldShowMagicWand
    if (!shouldShowMagicWand && autoConnect.value) {
        autoConnect.value = false
    }
}

const handleAutoConnect = () => {
    autoConnect.value = true
}

const handleRefreshRows = () => {
    ledgerGrid.value?.fetchData()
    emitter.emit(SACC_LINES_UPDATED)
}

const createPaymentTransaction = async (subAccountLine?: SubAccountLine) => {
    if (subAccountLine) {
        await registerPayments([subAccountLine], panelId.value + 1)
    } else {
        await registerPayments(chosenSubAccountLines.value.filter(s => s.invoiceUuid !== undefined), panelId.value + 1)
    }
}

const matchLines = async () => {
    if (!saccLineConnectionBuilder.isValid()) {
        return
    }
    await closePosts(saccLineConnectionBuilder.getConnections())
    chosenSubAccountLines.value = []
    saccLineConnectionBuilder.clear()
    handleRefreshRows()
}

</script>

<style scoped>

</style>